import React, { useEffect, useState } from "react";
import { Button, InputNumber, Select, Spin, Switch } from "antd";
import { toast } from "react-toastify";
import SidebarNav from "../sidebar";
import { getGlobalKycConfig, updateGlobalKycConfig } from "../../../service/api";
import PermissionGate from "../commoncomponent/PermissionGate";
import { usePermission } from "../../../hooks/usePermission";

const DEFAULT_SUB = { enabled: true, coolingPeriod: { value: 24, unit: "HOURS" } };
const DEFAULT_FORM = {
  inrDeposit:       { ...DEFAULT_SUB },
  inrWithdrawal:    { ...DEFAULT_SUB },
  cryptoDeposit:    { ...DEFAULT_SUB },
  cryptoWithdrawal: { ...DEFAULT_SUB },
};

const SUB_LABELS = [
  { key: "inrDeposit",       label: "INR Deposit" },
  { key: "inrWithdrawal",    label: "INR Withdrawal" },
  { key: "cryptoDeposit",    label: "Crypto Deposit" },
  { key: "cryptoWithdrawal", label: "Crypto Withdrawal" },
];

const KycSubConfigCard = ({ label, value, onChange, disabled }) => {
  const sub = value ?? DEFAULT_SUB;
  const cp  = sub.coolingPeriod ?? { value: 24, unit: "HOURS" };
  return (
    <div
      style={{
        border: "1px solid #e8e8e8",
        borderRadius: 8,
        padding: 20,
        background: "#fff",
      }}
    >
      <h6 style={{ marginBottom: 16 }}>{label}</h6>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <Switch
          checked={!!sub.enabled}
          onChange={(v) => onChange({ ...sub, enabled: v })}
          disabled={disabled}
        />
        <span style={{ fontSize: 13 }}>{sub.enabled ? "Enabled" : "Disabled"}</span>
      </div>

      <div>
        <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 6 }}>Cooling Period</div>
        <div style={{ display: "flex", gap: 8 }}>
          <InputNumber
            min={0}
            precision={0}
            value={cp.value}
            onChange={(v) =>
              onChange({ ...sub, coolingPeriod: { ...cp, value: v ?? 0 } })
            }
            style={{ width: 100 }}
            disabled={disabled}
          />
          <Select
            value={cp.unit}
            onChange={(u) =>
              onChange({ ...sub, coolingPeriod: { ...cp, unit: u } })
            }
            options={[
              { label: "Hours", value: "HOURS" },
              { label: "Days",  value: "DAYS"  },
            ]}
            style={{ width: 100 }}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};

export default function KycConfigPage() {
  const { canView } = usePermission();
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [form,    setForm]    = useState(DEFAULT_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getGlobalKycConfig();
      if (res?.data) {
        setForm({
          inrDeposit:       res.data.inrDeposit       ?? DEFAULT_SUB,
          inrWithdrawal:    res.data.inrWithdrawal    ?? DEFAULT_SUB,
          cryptoDeposit:    res.data.cryptoDeposit    ?? DEFAULT_SUB,
          cryptoWithdrawal: res.data.cryptoWithdrawal ?? DEFAULT_SUB,
        });
      }
    } catch {
      toast.error("Failed to load configuration", { toastId: "kyc-config-load" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await updateGlobalKycConfig(form);
      toast.success("Configuration saved", { toastId: "kyc-config-save" });
    } catch (e) {
      toast.error(e?.data?.message || "Failed to save configuration", { toastId: "kyc-config-save" });
    } finally {
      setSaving(false);
    }
  };

  const updateSub = (key) => (val) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  if (!canView("kyc-config")) {
    return (
      <>
        <SidebarNav />
        <div className="page-wrapper">
          <div className="content container-fluid" style={{ padding: 24 }}>
            <p>You do not have permission to view this page.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid" style={{ padding: 24 }}>
          <h3>KYC Configuration</h3>
          <p style={{ opacity: 0.7, marginBottom: 24 }}>
            Global defaults for deposit &amp; withdrawal enable/disable and cooling periods.
            These apply to all users unless overridden at the user level.
          </p>

          <Spin spinning={loading}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 20,
                marginBottom: 24,
              }}
            >
              {SUB_LABELS.map(({ key, label }) => (
                <KycSubConfigCard
                  key={key}
                  label={label}
                  value={form[key]}
                  onChange={updateSub(key)}
                  disabled={saving}
                />
              ))}
            </div>

            <PermissionGate module="kyc-config" action="update">
              <Button type="primary" loading={saving} onClick={save}>
                Save Configuration
              </Button>
            </PermissionGate>
          </Spin>
        </div>
      </div>
    </>
  );
}
